import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import { doc, getDoc, setDoc, collection, query, onSnapshot, orderBy, updateDoc, serverTimestamp } from 'firebase/firestore';
import toast from 'react-hot-toast';
import {
  Cog6ToothIcon,
  ArrowTopRightOnSquareIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';

// eslint-disable-next-line no-unused-vars
const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

export default function BookingPage() {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('upcoming');
  const [bookings, setBookings] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [sortBy, setSortBy] = useState('date');
  const [showSortMenu, setShowSortMenu] = useState(false);

  const tabs = [
    { id: 'upcoming', label: 'Upcoming' },
    { id: 'past', label: 'Past' },
    { id: 'calendar', label: 'Calendar' },
  ];

  const sortOptions = [
    { value: 'date', label: 'By date' },
    { value: 'name', label: 'By name' },
    { value: 'status', label: 'By status' },
  ];

  useEffect(() => {
    if (!currentUser?.uid) return;

    const bookingsRef = collection(db, 'users', currentUser.uid, 'bookings');
    const q = query(bookingsRef, orderBy('date', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setBookings(data);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!currentUser?.uid) return;

    const fetchSettings = async () => {
      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        setSettings(userDoc.data().bookingSettings || null);
      }
    };

    fetchSettings();
  }, [currentUser?.uid]);

  const updateBookingStatus = async (bookingId, status) => {
    try {
      const bookingRef = doc(db, 'users', currentUser.uid, 'bookings', bookingId);
      await updateDoc(bookingRef, {
        status,
        updatedAt: serverTimestamp(),
      });
      toast.success(`Booking ${status}`);
    } catch (error) {
      toast.error('Failed to update booking');
    }
  };

  const upcomingBookings = bookings.filter(b =>
    b.status !== 'cancelled' && b.status !== 'completed' &&
    new Date(`${b.date}T${b.time}`) >= new Date()
  ).sort((a, b) => {
    if (sortBy === 'name') return (a.clientName || '').localeCompare(b.clientName || '');
    if (sortBy === 'status') return (a.status || '').localeCompare(b.status || '');
    return new Date(`${a.date}T${a.time}`) - new Date(`${b.date}T${b.time}`);
  });

  const pastBookings = bookings.filter(b =>
    b.status === 'completed' || b.status === 'cancelled' || new Date(`${b.date}T${b.time}`) < new Date()
  );

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (timeStr) => {
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-white dark:bg-black">
        <div className="w-5 h-5 border-2 border-brand border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white dark:bg-black">
      {/* Header */}
      <div className="px-6 pt-5 pb-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setShowSettingsModal(true)}
              className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-white dark:bg-[#111111] border border-gray-200 dark:border-[#333] text-gray-700 dark:text-gray-200 hover:text-brand hover:border-brand/30 hover:shadow-sm transition-all text-sm font-medium"
            >
              <Cog6ToothIcon className="w-5 h-5" />
              <span>Settings</span>
            </motion.button>

            {settings?.slug && (
              <motion.a
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 }}
                href={`/book/${settings.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-white dark:bg-[#111111] border border-gray-200 dark:border-[#333] text-gray-700 dark:text-gray-200 hover:text-brand hover:border-brand/30 hover:shadow-sm transition-all text-sm font-medium"
              >
                <ArrowTopRightOnSquareIcon className="w-5 h-5" />
                <span>View Page</span>
              </motion.a>
            )}
          </div>
        </div>
      </div>

      {/* Tab navigation */}
      <div className="px-6 pb-3 flex-shrink-0">
        <div className="flex items-center gap-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`relative px-4 py-2 text-sm transition-all ${
                activeTab === tab.id
                  ? 'text-gray-900 dark:text-white'
                  : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
              }`}
            >
              {activeTab === tab.id && (
                <motion.div
                  layoutId="booking-tab-indicator"
                  className="absolute inset-0 bg-white/70 dark:bg-[#1A1A1A] rounded-lg border border-gray-200/50 dark:border-[#333]"
                  transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                />
              )}
              <span className="relative z-10 font-medium">{tab.label}</span>
              {tab.id === 'upcoming' && upcomingBookings.length > 0 && (
                <span className={`relative z-10 ml-1.5 text-xs ${
                  activeTab === tab.id ? 'text-gray-500 dark:text-gray-400' : 'text-gray-300 dark:text-gray-600'
                }`}>
                  {upcomingBookings.length}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 px-6 pb-4 min-h-0 flex flex-col">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex-1 min-h-0 bg-gray-50/50 dark:bg-[#0D0D0D] rounded-xl border border-gray-100 dark:border-[#222] flex flex-col overflow-hidden"
        >
          <AnimatePresence mode="wait">
            {activeTab === 'upcoming' && (
              <motion.div
                key="upcoming"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex-1 min-h-0 flex flex-col"
              >
                {upcomingBookings.length > 0 ? (
                  <>
                    <div className="px-5 py-2.5 border-b border-gray-100 dark:border-[#222] flex items-center justify-between flex-shrink-0">
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        {upcomingBookings.length} upcoming
                      </span>
                      <div className="relative">
                        <button
                          onClick={() => setShowSortMenu(!showSortMenu)}
                          className="flex items-center gap-1.5 px-2 py-1 text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded transition-colors"
                        >
                          <span>{sortOptions.find(s => s.value === sortBy)?.label}</span>
                          <ChevronDownIcon className="w-3 h-3" />
                        </button>
                        {showSortMenu && (
                          <div className="absolute right-0 top-full mt-1 bg-white dark:bg-[#1A1A1A] border border-gray-200 dark:border-[#333] rounded-lg shadow-lg py-1 z-10 min-w-[100px]">
                            {sortOptions.map(option => (
                              <button
                                key={option.value}
                                onClick={() => {
                                  setSortBy(option.value);
                                  setShowSortMenu(false);
                                }}
                                className={`w-full px-3 py-1.5 text-left text-xs transition-colors ${
                                  sortBy === option.value ? 'text-brand' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                                }`}
                              >
                                {option.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex-1 min-h-0 overflow-auto divide-y divide-gray-100/60 dark:divide-[#222]">
                      {upcomingBookings.map((booking, i) => (
                        <motion.div
                          key={booking.id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: i * 0.03 }}
                          className="flex items-center justify-between px-5 py-4 hover:bg-white/60 dark:hover:bg-[#111111] transition-colors"
                        >
                          <div className="flex items-center gap-4 flex-1 min-w-0">
                            <div className="w-10 h-10 rounded-xl bg-brand/10 dark:bg-brand/20 flex items-center justify-center flex-shrink-0">
                              <span className="text-sm font-semibold text-brand">
                                {new Date(booking.date).getDate()}
                              </span>
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                {booking.clientName}
                              </p>
                              <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                                {booking.serviceName} · {formatDate(booking.date)} at {formatTime(booking.time)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium capitalize ${
                              booking.status === 'confirmed' ? 'bg-emerald-50 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400' :
                              booking.status === 'pending' ? 'bg-amber-50 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400' :
                              'bg-gray-100 dark:bg-[#333] text-gray-500 dark:text-gray-400'
                            }`}>
                              {booking.status}
                            </span>
                            {booking.status === 'pending' && (
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => updateBookingStatus(booking.id, 'confirmed')}
                                  className="text-xs text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 font-medium"
                                >
                                  Confirm
                                </button>
                                <span className="text-gray-300 dark:text-gray-600">·</span>
                                <button
                                  onClick={() => updateBookingStatus(booking.id, 'cancelled')}
                                  className="text-xs text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400"
                                >
                                  Cancel
                                </button>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex flex-col items-center justify-center py-16 px-6">
                    <p className="text-base font-medium text-gray-700 dark:text-gray-200 mb-1">No upcoming bookings</p>
                    <p className="text-sm text-gray-400 dark:text-gray-500 mb-5">Share your booking page to start receiving appointments</p>
                    {settings?.slug && (
                      <motion.a
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        href={`/book/${settings.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-brand hover:bg-brand-hover rounded-full transition-all shadow-sm"
                      >
                        <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                        View Booking Page
                      </motion.a>
                    )}
                  </div>
                )}
              </motion.div>
            )}

            {activeTab === 'past' && (
              <motion.div
                key="past"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex-1 min-h-0 flex flex-col"
              >
                {pastBookings.length > 0 ? (
                  <div className="flex-1 min-h-0 overflow-auto divide-y divide-gray-100/60 dark:divide-[#222]">
                    {pastBookings.map((booking, i) => (
                      <motion.div
                        key={booking.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: i * 0.03 }}
                        className="flex items-center justify-between px-5 py-4 hover:bg-white/60 dark:hover:bg-[#111111] transition-colors opacity-60"
                      >
                        <div className="flex items-center gap-4 flex-1 min-w-0">
                          <div className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-[#222] flex items-center justify-center flex-shrink-0">
                            <span className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                              {new Date(booking.date).getDate()}
                            </span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {booking.clientName}
                            </p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                              {booking.serviceName} · {formatDate(booking.date)}
                            </p>
                          </div>
                        </div>
                        <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium capitalize ${
                          booking.status === 'completed' ? 'bg-gray-100 dark:bg-[#333] text-gray-500 dark:text-gray-400' :
                          booking.status === 'cancelled' ? 'bg-red-50 dark:bg-red-500/20 text-red-500 dark:text-red-400' :
                          'bg-gray-100 dark:bg-[#333] text-gray-500 dark:text-gray-400'
                        }`}>
                          {booking.status}
                        </span>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="flex-1 flex flex-col items-center justify-center py-16 px-6">
                    <p className="text-base font-medium text-gray-700 dark:text-gray-200 mb-1">No past bookings</p>
                    <p className="text-sm text-gray-400 dark:text-gray-500">Completed appointments will appear here</p>
                  </div>
                )}
              </motion.div>
            )}

            {activeTab === 'calendar' && (
              <motion.div
                key="calendar"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex-1 min-h-0 overflow-auto"
              >
                <CalendarView bookings={bookings} />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>

      <BookingSettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        settings={settings}
        userId={currentUser?.uid}
        onSave={(newSettings) => setSettings(newSettings)}
      />
    </div>
  );
}

function CalendarView({ bookings }) {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();

    const days = [];
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };

  const getBookingsForDate = (date) => {
    if (!date) return [];
    const dateStr = date.toISOString().split('T')[0];
    return bookings.filter(b => b.date === dateStr);
  };

  const days = getDaysInMonth(currentMonth);
  const monthName = currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
          className="p-2 hover:bg-gray-100 dark:hover:bg-[#222] rounded-lg transition-colors"
        >
          <ChevronLeftIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
        </button>
        <h3 className="text-sm font-medium text-gray-900 dark:text-white">{monthName}</h3>
        <button
          onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
          className="p-2 hover:bg-gray-100 dark:hover:bg-[#222] rounded-lg transition-colors"
        >
          <ChevronRightIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
        </button>
      </div>

      <div className="grid grid-cols-7 mb-2">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
          <div key={i} className="text-center text-xs text-gray-400 dark:text-gray-500 py-2">
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {days.map((date, index) => {
          const dayBookings = getBookingsForDate(date);
          const isToday = date && date.toDateString() === new Date().toDateString();

          return (
            <div
              key={index}
              className={`aspect-square p-1 rounded-lg ${
                !date ? '' : 'hover:bg-gray-100 dark:hover:bg-[#222] transition-colors'
              }`}
            >
              {date && (
                <div className="h-full flex flex-col">
                  <span className={`text-xs text-center ${
                    isToday ? 'w-6 h-6 bg-brand text-white rounded-full flex items-center justify-center mx-auto' : 'text-gray-700 dark:text-gray-300'
                  }`}>
                    {date.getDate()}
                  </span>
                  {dayBookings.length > 0 && (
                    <div className="flex justify-center mt-1 gap-0.5">
                      {dayBookings.slice(0, 3).map((_, i) => (
                        <div key={i} className="w-1 h-1 rounded-full bg-brand" />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BookingSettingsModal({ isOpen, onClose, settings, userId, onSave }) {
  const [formData, setFormData] = useState({
    slug: '',
    title: 'Book an Appointment',
    description: '',
    services: [],
    availability: {},
    bufferTime: 15,
    advanceBookingDays: 60,
    minNoticeHours: 24,
  });
  const [saving, setSaving] = useState(false);
  const [showAddService, setShowAddService] = useState(false);
  const [newService, setNewService] = useState({ name: '', duration: 60, price: '' });

  useEffect(() => {
    if (settings) {
      setFormData({
        slug: settings.slug || '',
        title: settings.title || 'Book an Appointment',
        description: settings.description || '',
        services: settings.services || [],
        availability: settings.availability || {},
        bufferTime: settings.bufferTime || 15,
        advanceBookingDays: settings.advanceBookingDays || 60,
        minNoticeHours: settings.minNoticeHours || 24,
      });
    }
  }, [settings]);

  const addService = () => {
    if (!newService.name || !newService.price) {
      toast.error('Name and price are required');
      return;
    }

    setFormData({
      ...formData,
      services: [
        ...formData.services,
        {
          id: Date.now().toString(),
          name: newService.name,
          duration: parseInt(newService.duration) || 60,
          price: parseFloat(newService.price) || 0,
        },
      ],
    });
    setNewService({ name: '', duration: 60, price: '' });
    setShowAddService(false);
  };

  const removeService = (id) => {
    setFormData({
      ...formData,
      services: formData.services.filter(s => s.id !== id),
    });
  };

  const handleSave = async () => {
    if (!formData.slug) {
      toast.error('Booking URL is required');
      return;
    }

    setSaving(true);
    try {
      const userRef = doc(db, 'users', userId);
      await setDoc(userRef, {
        bookingSettings: formData,
        bookingSlug: formData.slug,
      }, { merge: true });

      onSave(formData);
      toast.success('Settings saved');
      onClose();
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white dark:bg-[#111111] rounded-3xl w-full max-w-lg mx-4 max-h-[85vh] overflow-hidden shadow-2xl border border-transparent dark:border-[#222]"
      >
        <div className="p-6 border-b border-gray-100 dark:border-[#222] flex items-center justify-between">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">Booking Settings</h2>
          <button onClick={onClose} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[55vh] space-y-6">
          <div>
            <label className="block text-sm text-gray-500 dark:text-gray-400 mb-2">Booking URL</label>
            <div className="flex items-center">
              <span className="px-3 py-2.5 bg-gray-50 dark:bg-[#0D0D0D] border border-r-0 border-gray-200 dark:border-[#333] rounded-l-xl text-sm text-gray-400 dark:text-gray-500">
                klorah.app/book/
              </span>
              <input
                type="text"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                className="flex-1 px-4 py-2.5 border border-gray-200 dark:border-[#333] rounded-r-xl text-gray-900 dark:text-white bg-white dark:bg-[#0D0D0D] text-sm focus:outline-none focus:border-brand placeholder-gray-400 dark:placeholder-gray-600"
                placeholder="your-name"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-500 dark:text-gray-400 mb-2">Page Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2.5 border border-gray-200 dark:border-[#333] rounded-xl text-gray-900 dark:text-white bg-white dark:bg-[#0D0D0D] text-sm focus:outline-none focus:border-brand"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm text-gray-500 dark:text-gray-400">Services</label>
              <button
                onClick={() => setShowAddService(true)}
                className="text-xs text-brand hover:text-brand-hover font-medium"
              >
                + Add
              </button>
            </div>

            {showAddService && (
              <div className="mb-3 p-4 bg-gray-50 dark:bg-[#0D0D0D] rounded-xl space-y-3 border border-gray-200 dark:border-[#333]">
                <input
                  type="text"
                  placeholder="Service name"
                  value={newService.name}
                  onChange={(e) => setNewService({ ...newService, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 dark:border-[#333] rounded-lg text-sm bg-white dark:bg-[#1A1A1A] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:border-brand"
                />
                <div className="flex gap-2">
                  <input
                    type="number"
                    placeholder="Duration (min)"
                    value={newService.duration}
                    onChange={(e) => setNewService({ ...newService, duration: e.target.value })}
                    className="flex-1 px-3 py-2 border border-gray-200 dark:border-[#333] rounded-lg text-sm bg-white dark:bg-[#1A1A1A] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:border-brand"
                  />
                  <input
                    type="number"
                    placeholder="Price"
                    value={newService.price}
                    onChange={(e) => setNewService({ ...newService, price: e.target.value })}
                    className="flex-1 px-3 py-2 border border-gray-200 dark:border-[#333] rounded-lg text-sm bg-white dark:bg-[#1A1A1A] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:border-brand"
                  />
                </div>
                <div className="flex gap-2">
                  <button onClick={addService} className="px-4 py-2 bg-brand text-white text-sm rounded-lg hover:bg-brand-hover transition-colors">
                    Add
                  </button>
                  <button onClick={() => setShowAddService(false)} className="px-4 py-2 text-gray-500 dark:text-gray-400 text-sm hover:text-gray-700 dark:hover:text-gray-200 transition-colors">
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-2">
              {formData.services.map((service) => (
                <div key={service.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-[#0D0D0D] rounded-xl border border-gray-200 dark:border-[#333]">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{service.name}</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500">{service.duration} min · ${service.price}</p>
                  </div>
                  <button onClick={() => removeService(service.id)} className="text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 transition-colors">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
              {formData.services.length === 0 && !showAddService && (
                <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-4">No services added</p>
              )}
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-gray-100 dark:border-[#222] flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 border border-gray-200 dark:border-[#333] rounded-xl text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-[#1A1A1A] transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 px-4 py-2.5 bg-brand text-white text-sm font-medium rounded-xl hover:bg-brand-hover disabled:opacity-50 transition-colors"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
